'use client'

import { toast } from '@/core/lib/toast'
import { copyToClipboard } from '@/core/utils/clipboard'

import { api, requestId } from '@/features/veluvit/services/api'
import {
  ensureAccount,
  safe,
} from '@/features/veluvit/services/session.service'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { Bootstrap } from '@/features/veluvit/types/api.types'

/** 관심 작품을 정한 값으로 저장 — 실패하면 던진다(부르는 쪽이 낙관적 표시를 되돌린다) */
export async function setFavorite(workId: string, favorite: boolean) {
  await ensureAccount()
  const data = await api<Bootstrap>('/preference/', { workId, favorite })
  useSessionStore.getState().applyBootstrap(data)
}

/** 관심 작품 저장/해제 — 계정에 남는다 (v2 ACT.like) */
export const toggleFavorite = safe(async (workId: string) => {
  const liked = !!useSessionStore.getState().data?.favorites.includes(workId)
  await setFavorite(workId, !liked)
})

/** 같은 작품 조회를 다시 세지 않는 간격 — 상세를 오가거나 개발 모드에서 effect 가 두 번 돌아도 한 번만 */
const VIEW_GAP_MS = 30 * 60 * 1000
const lastViewed = new Map<string, number>()

/**
 * 작품 조회 기록 — 작품 상세를 열 때 한 번(POST /analytics/ { kind: 'work_view' }). 작가 랭킹 '최다 조회' · 작품 stats.views 가 이 값으로 집계된다
 * (2026-10-08 전엔 보내지 않아 늘 0이었다). 계정(게스트 포함)이 없으면 보내지 않는다 — 조회 하나 때문에 게스트를 만들지 않는다.
 * 기록 실패는 화면에 알리지 않는다(통계용).
 */
export function recordWorkView(workId: string) {
  if (!useSessionStore.getState().data?.user) return
  const now = Date.now()
  if (now - (lastViewed.get(workId) || 0) < VIEW_GAP_MS) return
  lastViewed.set(workId, now)
  void api('/analytics/', {
    kind: 'work_view',
    requestId: requestId(),
    workId,
  }).catch(() => undefined)
}

/** 링크 복사 — path 를 주면 그 화면 주소, 아니면 지금 주소 */
export const shareLink = safe(async (path?: string) => {
  await copyToClipboard(
    typeof path === 'string'
      ? new URL(path, window.location.origin).href
      : window.location.href
  )
  toast.show('공유 링크를 복사했어요')
})
